import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { Reveal } from "@/components/shared";
import { CTABand } from "@/pages/Home";
import { API } from "@/lib/api";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    API.get(`/articles/${slug}`).then(({ data }) => setArticle(data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="article-not-found">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-ivory">Article not found</h1>
          <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-sm text-gold-glow" data-testid="back-to-insights-link">
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
        </div>
      </main>
    );
  }

  if (!article) {
    return <main className="min-h-[70vh]" data-testid="article-loading" />;
  }

  return (
    <main data-testid="article-page">
      <section className="relative overflow-hidden pt-40 pb-16">
        <div className="absolute inset-0">
          <img src={article.cover_image} alt="" className="h-full w-full object-cover duotone opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy/85 to-navy" />
        </div>
        <div className="relative mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Link to="/insights" className="inline-flex items-center gap-2 text-xs font-medium text-gold-glow" data-testid="article-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> All insights
            </Link>
            <h1 className="mt-6 font-serif text-3xl tracking-tight text-ivory sm:text-4xl lg:text-5xl" style={{ lineHeight: 1.12 }}>
              {article.title}
            </h1>
            <div className="mt-6 flex items-center gap-5 text-xs text-slate-400">
              <span>{article.author}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {article.read_time}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <img src={article.cover_image} alt={article.title} className="h-72 w-full rounded-3xl border border-gold/20 object-cover duotone sm:h-96" />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 space-y-6" data-testid="article-body">
              {article.content.split(/\n+/).filter(Boolean).map((p, i) => (
                <p key={i} className={`leading-relaxed ${i === 0 ? "font-serif text-xl text-ivory sm:text-2xl" : "text-base font-light text-slate-300"}`}>
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Want this working for your business?"
        text="Book a free consultation and we will map these ideas onto your operation — in plain English."
      />
    </main>
  );
}
